import {
  OneWorkCardSkeleton,
  useHeaderHeightForScrollableContent,
  useWatchWindowResize,
} from '@diana/core';
import { useEffect, useRef, useState } from 'react';
import { GridOnScrollProps } from 'react-window';

const LoaderIdPrefix = '$loader-';

export const CardPaddingTopBottom = 16;

export function useVirtualBoardParams<T extends object>(props: IUseVirtualGridParamsProps<T>) {
  const {
    items,
    itemWidth,
    itemHeight,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = props;
  const headerHeight = useHeaderHeightForScrollableContent() - 10;
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const size = useWatchWindowResize({});
  const ref = useRef<HTMLDivElement>(null);

  const columnsCount = Math.floor(width / itemWidth);
  const loaderItemsCount = (items.length % columnsCount) + columnsCount;
  const loaderItems = Array.from({ length: loaderItemsCount }).map(
    (_, index) => ({ id: `${LoaderIdPrefix}${index}` } as unknown as T),
  );

  const allItems = isFetchingNextPage ? items.concat(loaderItems) : items;
  const rowsCount = Math.ceil(allItems.length / columnsCount);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.offsetHeight);
      setWidth(ref.current.offsetWidth);
    }
  }, [size, isLoading]);

  const onScroll = ({ scrollTop }: GridOnScrollProps) => {
    const lastItemOffset = (rowsCount - 1) * itemHeight;
    if (scrollTop + height >= lastItemOffset && !isFetchingNextPage) {
      hasNextPage && fetchNextPage();
    }
  };

  const getItemIndex = ({ columnIndex, rowIndex }: { columnIndex: number; rowIndex: number }) => {
    return rowIndex * columnsCount + columnIndex;
  };

  const isLoaderItem = (item: T & { id?: string }) => item?.id?.startsWith(LoaderIdPrefix);
  const renderLoaderItem = (props: { index: number }) => (
    <OneWorkCardSkeleton index={props.index} height={itemHeight - CardPaddingTopBottom} />
  );

  return {
    items: allItems,
    rowsCount,
    columnsCount,
    headerHeight,
    height,
    width,
    ref,
    onScroll,
    getItemIndex,
    isLoaderItem,
    renderLoaderItem,
  };
}

export interface IUseVirtualGridParamsProps<T extends object> {
  items: T[];
  itemHeight: number;
  itemWidth: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
}
